import { useState, useEffect } from "react";
import { motion } from "framer-motion";

const gridLinesV = [50, 120, 190, 260, 330, 400, 470, 540, 610, 680, 750, 820, 890, 960];
const gridLinesH = [120, 175, 230, 285, 340, 395, 450, 505, 560, 590];

const CELL_W = 65;
const CELL_H = 50;
const GAP = 5;
const ORIGIN_X = 50;
const ORIGIN_Y = 120;

function cellX(col: number) { return ORIGIN_X + col * (CELL_W + GAP); }
function cellY(row: number) { return ORIGIN_Y + row * (CELL_H + GAP); }
function cellCenterX(col: number) { return cellX(col) + CELL_W / 2; }
function cellCenterY(row: number) { return cellY(row) + CELL_H / 2; }

type CellGroup = "petrol" | "coral" | "turkos";

const groupColors: Record<CellGroup, { fill: string; fillAlt: string }> = {
  petrol: {
    fill: "hsl(186 100% 30% / 0.14)",
    fillAlt: "hsl(186 100% 40% / 0.10)",
  },
  coral: {
    fill: "hsl(0 100% 70% / 0.12)",
    fillAlt: "hsl(0 100% 70% / 0.08)",
  },
  turkos: {
    fill: "hsl(181 100% 33% / 0.14)",
    fillAlt: "hsl(181 100% 33% / 0.09)",
  },
};

interface Cell {
  col: number;
  row: number;
  group: CellGroup;
  alt?: boolean;
}

const cells: Cell[] = [
  // Petrol cluster
  { col: 0, row: 0, group: "petrol" },
  { col: 1, row: 0, group: "petrol" },
  { col: 2, row: 0, group: "petrol" },
  { col: 0, row: 1, group: "petrol" },
  { col: 1, row: 1, group: "petrol" },
  { col: 2, row: 1, group: "petrol" },
  { col: 0, row: 2, group: "petrol", alt: true },
  { col: 1, row: 2, group: "petrol", alt: true },
  { col: 0, row: 3, group: "petrol", alt: true },
  { col: 1, row: 3, group: "petrol" },
  { col: 2, row: 3, group: "petrol", alt: true },
  { col: 0, row: 4, group: "petrol", alt: true },
  { col: 1, row: 4, group: "petrol" },
  { col: 0, row: 5, group: "petrol", alt: true },
  // Coral cluster
  { col: 3, row: 1, group: "coral", alt: true },
  { col: 4, row: 0, group: "coral" },
  { col: 5, row: 0, group: "coral" },
  { col: 6, row: 0, group: "coral", alt: true },
  { col: 3, row: 2, group: "coral" },
  { col: 4, row: 1, group: "coral" },
  { col: 5, row: 1, group: "coral", alt: true },
  { col: 4, row: 2, group: "coral", alt: true },
  { col: 5, row: 2, group: "coral" },
  { col: 6, row: 2, group: "coral", alt: true },
  { col: 3, row: 3, group: "coral", alt: true },
  { col: 4, row: 3, group: "coral" },
  { col: 5, row: 3, group: "coral", alt: true },
  { col: 6, row: 1, group: "coral" },
  // Turkos cluster
  { col: 7, row: 0, group: "turkos" },
  { col: 8, row: 0, group: "turkos" },
  { col: 9, row: 0, group: "turkos", alt: true },
  { col: 7, row: 1, group: "turkos", alt: true },
  { col: 8, row: 1, group: "turkos" },
  { col: 9, row: 1, group: "turkos" },
  { col: 10, row: 1, group: "turkos", alt: true },
  { col: 8, row: 2, group: "turkos", alt: true },
  { col: 9, row: 2, group: "turkos" },
  { col: 10, row: 2, group: "turkos" },
  { col: 11, row: 2, group: "turkos", alt: true },
  { col: 9, row: 3, group: "turkos", alt: true },
  { col: 10, row: 3, group: "turkos" },
  { col: 11, row: 3, group: "turkos" },
  { col: 10, row: 4, group: "turkos", alt: true },
  { col: 11, row: 4, group: "turkos", alt: true },
];

const pins: Array<{
  cx: number;
  cy: number;
  delay: number;
  color: string;
  address?: string;
  valid?: boolean;
}> = [
  { cx: cellCenterX(1), cy: cellCenterY(0), delay: 1.0, color: "hsl(186 100% 50%)", address: "Storgatan 12, Stockholm", valid: true },
  { cx: cellCenterX(8), cy: cellCenterY(0), delay: 1.15, color: "hsl(181 100% 45%)", address: "Östra Hamng. 5, Göteborg", valid: true },
  { cx: cellCenterX(5), cy: cellCenterY(2), delay: 1.3, color: "hsl(0 100% 70%)", address: "Björkallén 1, Uppsala", valid: true },
  { cx: cellCenterX(1), cy: cellCenterY(3), delay: 1.45, color: "hsl(186 100% 50%)", address: "Tallvgen 3, Umeå", valid: false },
  { cx: cellCenterX(10), cy: cellCenterY(2), delay: 1.6, color: "hsl(181 100% 45%)", address: "Kungsvägen 8, Malmö", valid: true },
  { cx: cellCenterX(9), cy: cellCenterY(1), delay: 1.75, color: "hsl(181 100% 45%)", address: "Industriv. 22, Linköping", valid: true },
  { cx: cellCenterX(4), cy: cellCenterY(2), delay: 1.9, color: "hsl(0 100% 70%)" },
  { cx: cellCenterX(11), cy: cellCenterY(3), delay: 2.05, color: "hsl(181 100% 45%)" },
  { cx: cellCenterX(6), cy: cellCenterY(1), delay: 2.2, color: "hsl(0 100% 70%)" },
  { cx: cellCenterX(0), cy: cellCenterY(5), delay: 2.35, color: "hsl(186 100% 50%)" },
];

const routes = [
  { d: `M${cellCenterX(1)} ${cellCenterY(0)} Q${cellCenterX(3)} ${cellCenterY(0)}, ${cellCenterX(5)} ${cellCenterY(2)}`, delay: 1.8 },
  { d: `M${cellCenterX(8)} ${cellCenterY(0)} Q${cellCenterX(8.5)} ${cellCenterY(0.5)}, ${cellCenterX(9)} ${cellCenterY(1)}`, delay: 2.0 },
  { d: `M${cellCenterX(4)} ${cellCenterY(2)} Q${cellCenterX(4.5)} ${cellCenterY(2)}, ${cellCenterX(5)} ${cellCenterY(2)}`, delay: 2.1 },
  { d: `M${cellCenterX(5)} ${cellCenterY(2)} Q${cellCenterX(7)} ${cellCenterY(1)}, ${cellCenterX(8)} ${cellCenterY(0)}`, delay: 2.2 },
  { d: `M${cellCenterX(9)} ${cellCenterY(1)} Q${cellCenterX(9.5)} ${cellCenterY(1.5)}, ${cellCenterX(10)} ${cellCenterY(2)}`, delay: 2.3 },
  { d: `M${cellCenterX(1)} ${cellCenterY(3)} Q${cellCenterX(1)} ${cellCenterY(1.5)}, ${cellCenterX(1)} ${cellCenterY(0)}`, delay: 2.4 },
];

const coordLabels = [
  { x: cellCenterX(1) + 25, y: cellCenterY(0) - 8, text: "59.33°N", delay: 2.6 },
  { x: cellCenterX(8) + 25, y: cellCenterY(0) - 8, text: "57.71°N", delay: 2.7 },
  { x: cellCenterX(10) + 25, y: cellCenterY(2) - 8, text: "55.60°N", delay: 2.8 },
  { x: cellCenterX(5) + 25, y: cellCenterY(2) - 8, text: "60.67°N", delay: 2.9 },
];

function AddressLabel({ pin }: { pin: typeof pins[number] }) {
  if (!pin.address) return null;

  const labelDelay = pin.delay + 1.4;
  const checkDelay = labelDelay + 0.5;
  const isValid = pin.valid !== false;

  const labelX = pin.cx < 500 ? pin.cx + 24 : pin.cx - 170;
  const labelY = pin.cy - 18;

  return (
    <g>
      <motion.rect
        x={labelX - 6}
        y={labelY - 12}
        width={152}
        height={28}
        rx={4}
        fill="white"
        fillOpacity={0.08}
        stroke="white"
        strokeOpacity={0.12}
        strokeWidth={0.5}
        initial={{ opacity: 0, scaleX: 0 }}
        animate={{ opacity: 1, scaleX: 1 }}
        transition={{ delay: labelDelay, duration: 0.4, ease: "easeOut" }}
        style={{ transformOrigin: `${pin.cx < 500 ? labelX - 6 : labelX + 146}px ${labelY + 2}px` }}
      />
      <motion.text
        x={labelX}
        y={labelY + 2}
        fill="white"
        fillOpacity={0.65}
        fontSize={8.5}
        fontFamily="monospace"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: labelDelay + 0.2, duration: 0.3 }}
      >
        {pin.address}
      </motion.text>
      <motion.g
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{
          type: "spring",
          stiffness: 500,
          damping: 15,
          delay: checkDelay,
        }}
        style={{ transformOrigin: `${labelX + 140}px ${labelY + 1}px` }}
      >
        {isValid ? (
          <>
            <circle cx={labelX + 140} cy={labelY + 1} r={7} fill="hsl(145 70% 45% / 0.3)" stroke="hsl(145 70% 55%)" strokeWidth={1} />
            <path d={`M${labelX + 136} ${labelY + 1} l2.5 3 l4.5 -5`} stroke="hsl(145 80% 60%)" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </>
        ) : (
          <>
            <circle cx={labelX + 140} cy={labelY + 1} r={7} fill="hsl(0 80% 55% / 0.3)" stroke="hsl(0 80% 60%)" strokeWidth={1} />
            <path d={`M${labelX + 137} ${labelY - 2} l6 6 M${labelX + 143} ${labelY - 2} l-6 6`} stroke="hsl(0 80% 65%)" strokeWidth={1.5} strokeLinecap="round" fill="none" />
          </>
        )}
      </motion.g>
    </g>
  );
}

/**
 * CSS pulse ring — replaces Framer Motion infinite animations to avoid
 * continuous JS frame scheduling after the entrance animation completes.
 */
function PulseRing({ cx, cy, color, delayMs }: { cx: number; cy: number; color: string; delayMs: number }) {
  return (
    <>
      <circle
        cx={cx} cy={cy} r={16}
        fill="none" stroke={color} strokeOpacity={0.4} strokeWidth={1.5}
        className="animate-hero-pulse-ring"
        style={{ transformOrigin: `${cx}px ${cy}px`, animationDelay: `${delayMs}ms` }}
      />
      <circle
        cx={cx} cy={cy} r={16}
        fill="none" stroke={color} strokeOpacity={0.2} strokeWidth={1}
        className="animate-hero-pulse-ring-slow"
        style={{ transformOrigin: `${cx}px ${cy}px`, animationDelay: `${delayMs + 600}ms` }}
      />
    </>
  );
}

export function HeroBackgroundAnimation() {
  // Defer rendering until after LCP by waiting for idle callback
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if ("requestIdleCallback" in window) {
      const id = requestIdleCallback(() => setReady(true), { timeout: 800 });
      return () => cancelIdleCallback(id);
    } else {
      const id = setTimeout(() => setReady(true), 100);
      return () => clearTimeout(id);
    }
  }, []);

  if (!ready) return null;

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      <svg
        viewBox="0 0 1040 600"
        preserveAspectRatio="xMidYMid slice"
        className="absolute inset-0 w-full h-full"
        fill="none"
        role="presentation"
      >
        {/* Grid lines */}
        {gridLinesV.map((x, i) => (
          <motion.line
            key={`v-${i}`}
            x1={x} y1={0} x2={x} y2={600}
            stroke="white"
            strokeOpacity={0.06}
            strokeWidth={0.5}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: i * 0.03, duration: 0.3 }}
          />
        ))}
        {gridLinesH.map((y, i) => (
          <motion.line
            key={`h-${i}`}
            x1={0} y1={y} x2={1040} y2={y}
            stroke="white"
            strokeOpacity={0.06}
            strokeWidth={0.5}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: i * 0.03, duration: 0.3 }}
          />
        ))}

        {/* Grid cells */}
        {cells.map((cell, i) => {
          const colors = groupColors[cell.group];
          const fill = cell.alt ? colors.fillAlt : colors.fill;
          return (
            <motion.rect
              key={`cell-${i}`}
              x={cellX(cell.col)}
              y={cellY(cell.row)}
              width={CELL_W}
              height={CELL_H}
              rx={3}
              fill={fill}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 + i * 0.02, duration: 0.4 }}
            />
          );
        })}

        {/* Curved route lines */}
        {routes.map((route, i) => (
          <motion.path
            key={`route-${i}`}
            d={route.d}
            stroke="white"
            strokeOpacity={0.1}
            strokeWidth={1}
            strokeDasharray="8 5"
            fill="none"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 1.4, delay: route.delay, ease: "easeOut" }}
          />
        ))}

        {/* Map pins — entrance via Framer Motion, pulse rings via CSS */}
        {pins.map((pin, i) => (
          <g key={`pin-${i}`}>
            {/* CSS-driven pulse rings (no JS per frame) */}
            <PulseRing cx={pin.cx} cy={pin.cy} color={pin.color} delayMs={(pin.delay + 0.6) * 1000} />

            {/* Static glow (entrance only) */}
            <motion.circle
              cx={pin.cx} cy={pin.cy} r={16}
              fill={pin.color.replace(")", " / 0.15)")}
              initial={{ scale: 0 }}
              animate={{ scale: [0, 1.5, 1] }}
              transition={{ duration: 0.6, delay: pin.delay }}
              style={{ transformOrigin: `${pin.cx}px ${pin.cy}px` }}
            />
            {/* Pin dot (entrance only) */}
            <motion.circle
              cx={pin.cx} cy={pin.cy} r={5}
              fill={pin.color}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 400, damping: 12, delay: pin.delay + 0.1 }}
              style={{ transformOrigin: `${pin.cx}px ${pin.cy}px` }}
            />
          </g>
        ))}

        {/* Address validation labels */}
        {pins.map((pin, i) => (
          <AddressLabel key={`addr-${i}`} pin={pin} />
        ))}

        {/* Coordinate labels */}
        {coordLabels.map((label, i) => (
          <motion.text
            key={`coord-${i}`}
            x={label.x} y={label.y}
            fill="white" fillOpacity={0.2} fontSize={9} fontFamily="monospace"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: label.delay, duration: 0.5 }}
          >
            {label.text}
          </motion.text>
        ))}
      </svg>
    </div>
  );
}
